import { useEffect, useState } from 'react';
import { WALLBOARD_WIDGETS, CAMPAIGN_WIDGETS, type WallboardLayout, type WallboardWidget, type WallboardWidgetType } from '@arcnet/contracts';
import { useSession } from '../../lib/auth/session.js';
import { Icon } from '../../components/ui/Icon.js';
import { ErrorBanner } from '../../components/ui/Primitives.js';
import { BoardGrid, widget, rowsOf, type DrawContext } from './widgets.js';

/**
 * Building a wallboard (owner, 26 September 2026: "build a wall board based off of a list of items
 * and move them around and be able to save the board"). Widgets are added from the list, dragged
 * into order (or moved with the arrows), given a queue -- or every queue -- and a width, and the
 * board is saved to the account for anyone with queue access to open.
 */

const GROUPS = ['Now', 'Today', 'Lists', 'Charts', 'Dialler', 'Board'] as const;
const TYPES = Object.keys(WALLBOARD_WIDGETS) as WallboardWidgetType[];

export function BoardEditor({ initialName, initial, ctx, saving, error, onSave, onCancel, onDelete }: {
  initialName: string; initial: WallboardLayout; ctx: Omit<DrawContext, 'layout'>;
  saving: boolean; error: unknown;
  onSave: (name: string, layout: WallboardLayout) => void; onCancel: () => void; onDelete?: (() => void) | undefined;
}) {
  const can = useSession((s) => s.can);
  const [name, setName] = useState(initialName);
  const [layout, setLayout] = useState<WallboardLayout>(initial);
  const [queue, setQueue] = useState<string | null>(ctx.queues[0]?.key ?? null);
  // The queues arrive after the first draw: default to the first one once they have.
  const firstQueue = ctx.queues[0]?.key ?? null;
  const [picked, setPicked] = useState(false);
  useEffect(() => { if (!picked && firstQueue) setQueue(firstQueue); }, [firstQueue, picked]);
  const [drag, setDrag] = useState<number | null>(null);
  const [over, setOver] = useState<number | null>(null);
  const [showThresholds, setShowThresholds] = useState(false);

  const set = (widgets: WallboardWidget[]) => setLayout({ ...layout, widgets });
  const change = (i: number, patch: Partial<WallboardWidget>) => set(layout.widgets.map((w, j) => (j === i ? { ...w, ...patch } : w)));
  const move = (from: number, to: number) => {
    if (to < 0 || to >= layout.widgets.length || from === to) return;
    const next = [...layout.widgets];
    const [w] = next.splice(from, 1);
    next.splice(to, 0, w!);
    set(next);
  };
  const add = (type: WallboardWidgetType) => set([...layout.widgets, widget(type, queue, type === 'heading' ? { title: 'Heading' } : {})]);

  return (
    <div className="arc-stack" style={{ gap: 14 }}>
      <div className="arc-card">
        <div className="arc-card-body arc-row" style={{ gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div className="arc-field" style={{ margin: 0, minWidth: 220, flex: '1 1 220px' }}>
            <label className="arc-label" htmlFor="wb-name">Board name</label>
            <input id="wb-name" className="arc-input" value={name} maxLength={60} onChange={(e) => setName(e.target.value)} placeholder="Support floor" />
          </div>
          <div className="arc-field" style={{ margin: 0 }}>
            <label className="arc-label" htmlFor="wb-q">New widgets show</label>
            <select id="wb-q" className="arc-select" value={queue ?? ''} onChange={(e) => { setPicked(true); setQueue(e.target.value || null); }}>
              {ctx.queues.map((q) => <option key={q.key} value={q.key}>{q.name}</option>)}
              <option value="">All queues</option>
            </select>
          </div>
          <button type="button" className="arc-btn arc-btn-sm" onClick={() => setShowThresholds((v) => !v)}>
            <Icon name="settings" size={13} /> Colours and targets
          </button>
          <div className="arc-spacer" />
          {onDelete && <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm" onClick={onDelete}>Delete board</button>}
          <button type="button" className="arc-btn arc-btn-sm" onClick={onCancel}>Cancel</button>
          <button type="button" className="arc-btn arc-btn-primary arc-btn-sm" disabled={!name.trim() || layout.widgets.length === 0 || saving}
            onClick={() => onSave(name.trim(), layout)}>{saving ? 'Saving…' : 'Save board'}</button>
        </div>
        {showThresholds && (
          <div className="arc-card-body arc-row" style={{ gap: 14, flexWrap: 'wrap', paddingTop: 0 }}>
            {([
              ['sl_target_sec', 'Answered within (s)'], ['sl_red_pct', 'Service level below (%)'],
              ['wait_amber_sec', 'Longest wait amber (s)'], ['wait_red_sec', 'Longest wait red (s)'],
              ['waiting_amber', 'Waiting amber (callers)'], ['waiting_red', 'Waiting red (callers)'],
            ] as Array<[keyof WallboardLayout, string]>).map(([k, label]) => (
              <div key={k} className="arc-field" style={{ margin: 0, width: 160 }}>
                <label className="arc-label" htmlFor={`wbt-${k}`}>{label}</label>
                <input id={`wbt-${k}`} className="arc-input arc-num" inputMode="numeric" value={String(layout[k])}
                  onChange={(e) => { const n = Number(e.target.value.replace(/\D/g, '')); if (n > 0) setLayout({ ...layout, [k]: n }); }} />
              </div>
            ))}
          </div>
        )}
        {!!error && <div className="arc-card-body" style={{ paddingTop: 0 }}><ErrorBanner error={error} /></div>}
      </div>

      <div className="arc-row" style={{ gap: 14, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        {/* ---------------------------------------------------- the list */}
        <div className="arc-card" style={{ width: 220, flex: 'none', padding: 12 }}>
          {GROUPS.filter((g) => g !== 'Dialler' || can('campaign:read')).map((g) => (
            <div key={g} style={{ marginBottom: 10 }}>
              <div className="arc-label" style={{ marginBottom: 4 }}>{g}</div>
              <div className="arc-stack" style={{ gap: 4 }}>
                {TYPES.filter((t) => WALLBOARD_WIDGETS[t].group === g).map((t) => (
                  <button key={t} type="button" className="arc-btn arc-btn-ghost arc-btn-sm" style={{ justifyContent: 'flex-start' }} onClick={() => add(t)}>
                    <Icon name="plus" size={12} /> {WALLBOARD_WIDGETS[t].label}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* --------------------------------------------------- the board */}
        <div style={{ flex: '1 1 480px', minWidth: 0 }}>
          {layout.widgets.length === 0 ? (
            <div className="arc-card"><div className="arc-card-body arc-muted">Add widgets from the list. Drag them to reorder.</div></div>
          ) : (
            <BoardGrid widgets={layout.widgets} ctx={{ ...ctx, layout }} wrap={(w, i, body) => (
              <div draggable
                onDragStart={(e) => { setDrag(i); e.dataTransfer.effectAllowed = 'move'; }}
                onDragOver={(e) => { e.preventDefault(); setOver(i); }}
                onDragLeave={() => setOver((o) => (o === i ? null : o))}
                onDrop={(e) => { e.preventDefault(); if (drag !== null) move(drag, i); setDrag(null); setOver(null); }}
                onDragEnd={() => { setDrag(null); setOver(null); }}
                style={{ outline: over === i && drag !== i ? '2px dashed var(--arc-accent)' : '1px dashed var(--arc-border)', borderRadius: 'var(--arc-r)', padding: 6, opacity: drag === i ? 0.5 : 1, height: '100%', display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div className="arc-row" style={{ gap: 2, fontSize: 12 }}>
                  <span title="Drag to move" style={{ cursor: 'grab', padding: '0 4px' }} className="arc-subtle">⠿</span>
                  <strong style={{ flex: 1, fontSize: 12, minWidth: 0 }} className="arc-truncate" title={WALLBOARD_WIDGETS[w.type].label}>{WALLBOARD_WIDGETS[w.type].label}</strong>
                  <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm arc-btn-icon" aria-label="Move earlier" title="Move earlier" onClick={() => move(i, i - 1)} disabled={i === 0}>
                    <Icon name="chevronLeft" size={12} /></button>
                  <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm arc-btn-icon" aria-label="Move later" title="Move later" onClick={() => move(i, i + 1)} disabled={i === layout.widgets.length - 1}>
                    <Icon name="chevronRight" size={12} /></button>
                  <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm arc-btn-icon arc-btn-danger-ghost" aria-label="Remove" title="Remove"
                    onClick={() => set(layout.widgets.filter((_, j) => j !== i))}><Icon name="trash" size={12} /></button>
                </div>
                <div className="arc-row" style={{ gap: 4 }}>
                  {WALLBOARD_WIDGETS[w.type].queue && (
                    <select className="arc-select" style={{ flex: 1, minWidth: 0, padding: '2px 6px', fontSize: 12 }} aria-label="Queue" value={w.queue ?? ''}
                      onChange={(e) => change(i, { queue: e.target.value || null })}>
                      {ctx.queues.map((q) => <option key={q.key} value={q.key}>{q.name}</option>)}
                      <option value="">All queues</option>
                    </select>
                  )}
                  {CAMPAIGN_WIDGETS.has(w.type) && (
                    <select className="arc-select" style={{ flex: 1, minWidth: 0, padding: '2px 6px', fontSize: 12 }} aria-label="Campaign" value={w.campaign ?? ''}
                      onChange={(e) => change(i, { campaign: e.target.value || null })}>
                      <option value="">All campaigns</option>
                      {(ctx.campaigns ?? []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  )}
                  <select className="arc-select" style={{ width: 'auto', flex: WALLBOARD_WIDGETS[w.type].queue || CAMPAIGN_WIDGETS.has(w.type) ? 'none' : 1, padding: '2px 6px', fontSize: 12 }} aria-label="Width" value={w.span}
                    onChange={(e) => change(i, { span: Number(e.target.value) })}>
                    {[1, 2, 3, 4].map((s) => <option key={s} value={s}>{s === 4 ? 'Full width' : `${s} of 4 wide`}</option>)}
                  </select>
                  <select className="arc-select" style={{ width: 'auto', flex: 'none', padding: '2px 6px', fontSize: 12 }} aria-label="Height" value={rowsOf(w)}
                    onChange={(e) => change(i, { rows: Number(e.target.value) })}>
                    {[1, 2, 3, 4].map((r) => <option key={r} value={r}>{r === 1 ? '1 row high' : `${r} rows high`}</option>)}
                  </select>
                </div>
                {w.type === 'heading' && (
                  <input className="arc-input" value={w.title ?? ''} maxLength={80} placeholder="Heading" onChange={(e) => change(i, { title: e.target.value })} />
                )}
                <div style={{ flex: 1, pointerEvents: 'none' }}>{body}</div>
              </div>
            )} />
          )}
        </div>
      </div>
    </div>
  );
}
